import { VERSION } from '@chk/env-variables';
import { getBrowserBodyFontSize, isDarkMode } from '@utility/checkout-utils';
import { getRedirectCallbackName } from '@utility/volt-bridge';

import type { CommonEventsDefinition } from './common-events.types';
import type {
  DataLayerIntegrationInterface,
  DataLayerInterface,
  OptionalGlobalEventProperties,
  RequiredOnInitGlobalEventProperties,
  TrackedEvent,
} from './DataLayer.types';

export class DataLayer<
  CustomEventsDefinition extends Record<string, unknown> = Record<never, never>,
  CustomContextProperties extends Record<string, unknown> = Record<
    never,
    never
  >,
> implements DataLayerInterface<CustomEventsDefinition> {
  private integrations: DataLayerIntegrationInterface[] = [];
  private globalProperties: Record<string, unknown> = {};
  // eslint-disable-next-line -- any added because getInstance passes proper types for whole class / all methods and instance is used only inside getInstance
  private static instance: DataLayer<any, any> | null = null;
  private hasSetupIntegration = false;

  public static getInstance<
    T extends Record<string, unknown> = Record<never, never>,
    K extends Record<string, unknown> = Record<never, never>,
  >(): DataLayer<T, K> {
    DataLayer.instance ??= new DataLayer<T, K>();

    return DataLayer.instance;
  }

  public addIntegration(
    integration: DataLayerIntegrationInterface
  ): DataLayer<CustomEventsDefinition, CustomContextProperties> {
    this.integrations.push(integration);

    return this;
  }

  public init(
    properties: RequiredOnInitGlobalEventProperties &
      OptionalGlobalEventProperties
  ): void {
    if (this.integrations.length === 0) {
      return console.warn('There is no added integration to initialisation');
    }

    const bridgeCallbackName = getRedirectCallbackName();

    this.setContextProperties({
      browserFontSize: getBrowserBodyFontSize(),
      isVoltBridge: Boolean(bridgeCallbackName),
      bridgeCallbackName,
      release: VERSION,
      isDarkMode: isDarkMode(),
      ...properties,
    });

    this.hasSetupIntegration = true;
    this.integrations.forEach((integration) => {
      integration.init();

      integration.addGlobalEventProperties(this.globalProperties);
    });
  }

  public trackEvent({
    name,
    options,
    properties,
  }: TrackedEvent<CustomEventsDefinition, CommonEventsDefinition>) {
    if (!this.hasSetupIntegration) {
      console.warn('There is no integration setup');
    }

    this.integrations.forEach((integration) =>
      integration.trackEvent({
        name: name as string,
        properties: properties as Record<string, unknown>,
        options,
        globalProperties: this.globalProperties,
      })
    );

    return this;
  }

  public trackIdentification(identity: string) {
    this.integrations.forEach((integration) =>
      integration.trackIdentification(identity)
    );

    return this;
  }

  public trackView(name: string) {
    if (!this.hasSetupIntegration) {
      console.warn('There is no integration setup');
    }

    this.integrations.forEach((integration) => integration.trackView(name));

    return this;
  }

  public addGlobalEventProperties(
    properties: OptionalGlobalEventProperties & Partial<CustomContextProperties>
  ) {
    this.setContextProperties(properties);

    this.integrations.forEach((integration) =>
      integration.addGlobalEventProperties(this.globalProperties)
    );

    return this;
  }

  private setContextProperties(properties: Record<string, unknown>) {
    Object.assign(this.globalProperties, properties);
  }

  public static clearInstance() {
    DataLayer.instance = null;

    return this;
  }
}
